import { useRef, useEffect } from 'react';
import type { Position, MoveOptions } from '../types';
import { ANIMATION_CONFIG, TIMING } from '../config';
import {
  buildPathD,
  setMotionPath,
  setOffsetDistance,
  setOffsetRotate,
  setOffsetAnchor,
} from '../utils';
import { AvatarState } from '@wix/aria-ai-avatar';

export const useDotAnimation = (
  moveToPosition: (position: Position) => void,
  setAvatarHidden: (hidden: boolean) => void,
  triggerAvatarAnimationState: (state: AvatarState) => void,
) => {
  const leadDotRef = useRef<HTMLDivElement | null>(null);
  const trailRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isMountedRef = useRef(true);

  const animateDotsAlongPath = async (
    start: Position,
    end: Position,
    options: MoveOptions = {},
  ): Promise<void> => {
    if (!isMountedRef.current) {
      return;
    }

    const lead = leadDotRef.current;
    if (!lead) {
      return;
    }

    if (!options.skipShrink) {
      try {
        triggerAvatarAnimationState(AvatarState.SMALL);
      } catch {}
    }

    trailRefs.current.forEach((d, i) => {
      if (!d) {
        return;
      }
      const ratio = (i + 1) / ANIMATION_CONFIG.TRAIL_COUNT;
      const scale =
        ANIMATION_CONFIG.TRAIL_MIN_SCALE +
        (1 - ANIMATION_CONFIG.TRAIL_MIN_SCALE) * (1 - ratio);
      const opacity =
        ANIMATION_CONFIG.TRAIL_FADE +
        (1 - ANIMATION_CONFIG.TRAIL_FADE) * (1 - ratio);
      d.style.opacity = String(opacity);
      d.style.transform = `scale(${scale})`;
    });

    lead.style.opacity = '1';
    lead.style.background = '#000000';

    lead.animate(
      [{ background: '#000000' }, { background: ANIMATION_CONFIG.DOT_COLOR }],
      {
        duration: TIMING.DOT_FADE_DURATION,
        easing: 'ease-out',
        fill: 'forwards',
      },
    );

    const pathD = buildPathD(start, end);

    const prepDot = (el: HTMLElement) => {
      if (!el) {
        return;
      }
      setMotionPath(el, pathD);
      setOffsetDistance(el, '0%');
      setOffsetRotate(el, '0deg');
      setOffsetAnchor(el, '50% 50%');
    };

    prepDot(lead);

    trailRefs.current.forEach((td) => td && prepDot(td));

    const leadAnim = lead.animate(
      [{ offsetDistance: '0%' }, { offsetDistance: '100%' }],
      {
        duration: TIMING.TRAVEL_DURATION,
        easing: ANIMATION_CONFIG.EASING,
        fill: 'forwards',
      },
    );

    trailRefs.current.forEach((td, i) => {
      if (!td) {
        return;
      }
      td.animate([{ offsetDistance: '0%' }, { offsetDistance: '100%' }], {
        duration: TIMING.TRAVEL_DURATION,
        delay: (i + 1) * ANIMATION_CONFIG.TRAIL_STAGGER,
        easing: ANIMATION_CONFIG.EASING,
        fill: 'forwards',
      });
    });

    await leadAnim.finished;

    lead.style.opacity = '0';
    trailRefs.current.forEach((td) => td && (td.style.opacity = '0'));
  };

  const hideDots = () => {
    const lead = leadDotRef.current;
    if (lead) {
      lead.style.opacity = '0';
    }
    trailRefs.current.forEach((td) => {
      if (td) {
        td.style.opacity = '0';
      }
    });
  };

  const waitForNextFrame = () =>
    new Promise<void>((r) => requestAnimationFrame(() => r()));

  const moveFromOffsetWithDots = async (
    currentPosition: Position,
    options: MoveOptions = {},
  ): Promise<void> => {
    if (
      typeof currentPosition.x !== 'number' ||
      typeof currentPosition.y !== 'number'
    ) {
      console.log('[Astro] Invalid position, skipping animation');
      return;
    }

    const home = { x: 0, y: 0 };
    await animateDotsAlongPath(currentPosition, home, options);

    moveToPosition(home);
    setAvatarHidden(false);

    await waitForNextFrame();

    if (isMountedRef.current) {
      if (options.endState) {
        await triggerAvatarAnimationState(options.endState);
      } else {
        await triggerAvatarAnimationState(AvatarState.IDLE);
      }
    }
  };

  const moveToOffsetWithDots = async (
    targetPosition: Position,
    options: MoveOptions = {},
  ): Promise<void> => {
    if (
      typeof targetPosition.x !== 'number' ||
      typeof targetPosition.y !== 'number'
    ) {
      console.log('[Astro] Invalid position, skipping animation');
      return;
    }

    setAvatarHidden(true);
    await waitForNextFrame();

    const home = { x: 0, y: 0 };
    await animateDotsAlongPath(home, targetPosition, options);

    hideDots();
  };

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    leadDotRef,
    trailRefs,
    moveFromOffsetWithDots,
    moveToOffsetWithDots,
    hideDots,
  };
};
